import type { ThresholdBreach, VitalMetric } from './types';

/**
 * Pure grouping for the doctor's out-of-range list. No React, no API — the page
 * already holds every breach the window returned, so grouping and filtering are
 * local.
 *
 * Unlike the shifts grid there is no "ungrouped" bucket: every breach carries a
 * patient, a metric and a visit, so no row can fall through whichever dimension
 * is active.
 */

export type BreachGroupBy = 'patient' | 'metric' | 'visit' | 'none';

export type BreachGroupPrefs = {
  groupBy: BreachGroupBy;
  /** Empty means no filter — every group is shown. It never means "show nothing". */
  selectedGroupIds: string[];
  /**
   * EXPANDED, not collapsed — the inverse of the shifts module, and deliberately.
   * The screen opens folded, so an id nobody has a preference for must read as
   * collapsed; storing the collapsed ones would make every group that has never
   * been touched — including a patient who breaches for the first time tomorrow —
   * arrive open.
   */
  expandedGroupIds: string[];
};

export type BreachSection = {
  id: string;
  name: string;
  /** The date under a visit's name. Absent for the other dimensions. */
  subtitle?: string;
  breaches: ThresholdBreach[];
  collapsed: boolean;
};

export type GroupedBreaches = {
  /** Every breach in display order, for the ungrouped rendering. */
  rows: ThresholdBreach[];
  /** Empty when `groupBy` is 'none'. */
  sections: BreachSection[];
};

export type BreachLabels = {
  metric: (metric: VitalMetric) => string;
  patient: (breach: ThresholdBreach) => string;
  date: (iso: string) => string;
};

/**
 * The section a breach belongs to under each dimension.
 *
 * PREFIXED BY DIMENSION so a stored id can never be read against the wrong one:
 * a client id and a visit id are both bare integers, and without the prefix a
 * selection made while grouped by patient would silently match visits after a
 * switch.
 */
const sectionIdFor = (breach: ThresholdBreach, groupBy: BreachGroupBy): string => {
  switch (groupBy) {
    case 'patient':
      return `p:${breach.clientId}`;
    case 'metric':
      return `m:${breach.metric}`;
    case 'visit':
      return `v:${breach.visitId}`;
    default:
      return '';
  }
};

const timeOf = (breach: ThresholdBreach): number => {
  const ts = Date.parse(breach.measuredAt);
  // An unparseable date sorts oldest rather than throwing the whole comparison
  // off with NaN, which compares false against everything.
  return Number.isFinite(ts) ? ts : 0;
};

const newest = (breaches: ThresholdBreach[]): number =>
  breaches.reduce((max, breach) => Math.max(max, timeOf(breach)), 0);

export type BreachFilterOption = {
  label: string;
  value: string;
  /**
   * The patient behind this option. Set for a patient AND for a visit, because a
   * visit is somebody's visit — carried so the picker can show the same
   * avatar-and-name every other list shows, rather than splitting the label back
   * apart on a space, which a two-part Greek surname does not survive.
   */
  person?: { firstName: string; lastName: string };
  /** The date under a visit's name. Absent for the other dimensions. */
  subtitle?: string;
};

/** The options the filter offers for a dimension: the groups this data actually has. */
export const breachFilterOptions = (
  breaches: ThresholdBreach[],
  groupBy: BreachGroupBy,
  labels: BreachLabels
): BreachFilterOption[] => {
  if (groupBy === 'none') return [];
  const seen = new Map<string, BreachFilterOption>();
  for (const breach of breaches) {
    const id = sectionIdFor(breach, groupBy);
    if (seen.has(id)) continue;

    const isPerson = groupBy === 'patient' || groupBy === 'visit';
    // A VISIT IS NAMED FOR ITS PATIENT, so three visits of the same patient are
    // three identical options. The date is what tells them apart, and it goes in
    // the LABEL rather than only under it: the label is what the chosen chip
    // shows and what a typed search matches, and both of those are useless if
    // every option reads the same.
    const subtitle = groupBy === 'visit' ? labels.date(breach.measuredAt) : undefined;
    const name = sectionName(breach, groupBy, labels);

    seen.set(id, {
      value: id,
      label: subtitle ? `${name} · ${subtitle}` : name,
      person: isPerson ? { firstName: breach.clientFirstName ?? '', lastName: breach.clientLastName ?? '' } : undefined,
      subtitle,
    });
  }
  return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
};

const sectionName = (breach: ThresholdBreach, groupBy: BreachGroupBy, labels: BreachLabels): string => {
  switch (groupBy) {
    case 'metric':
      return labels.metric(breach.metric);
    case 'visit':
      return labels.patient(breach);
    default:
      return labels.patient(breach);
  }
};

export function groupBreaches(
  breaches: ThresholdBreach[],
  prefs: BreachGroupPrefs,
  labels: BreachLabels
): GroupedBreaches {
  const { groupBy } = prefs;

  // A selected id that names no group in the current data is dropped rather than
  // matching nobody: prefs outlive a window change, and a stale id left in the
  // set would empty the screen with no way to see why.
  const present = new Set(breaches.map((breach) => sectionIdFor(breach, groupBy)));
  const selected = new Set(prefs.selectedGroupIds.filter((id) => present.has(id)));
  const visible =
    selected.size === 0 ? breaches : breaches.filter((breach) => selected.has(sectionIdFor(breach, groupBy)));

  const byRecency = [...visible].sort((a, b) => timeOf(b) - timeOf(a));

  if (groupBy === 'none') {
    return { rows: byRecency, sections: [] };
  }

  const expanded = new Set(prefs.expandedGroupIds);
  const sections = new Map<string, BreachSection>();

  for (const breach of byRecency) {
    const id = sectionIdFor(breach, groupBy);
    let section = sections.get(id);
    if (!section) {
      section = {
        id,
        name: sectionName(breach, groupBy, labels),
        subtitle: groupBy === 'visit' ? labels.date(breach.measuredAt) : undefined,
        breaches: [],
        collapsed: !expanded.has(id),
      };
      sections.set(id, section);
    }
    section.breaches.push(breach);
  }

  // Most out-of-range readings first: the screen leads with where attention is
  // most needed. Ties go to the most recent reading, then to the name, so the
  // order is total and a re-render never reshuffles equal groups.
  const ordered = [...sections.values()].sort(
    (a, b) =>
      b.breaches.length - a.breaches.length || newest(b.breaches) - newest(a.breaches) || a.name.localeCompare(b.name)
  );

  return { rows: byRecency, sections: ordered };
}
